import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import * as Tabs from '@radix-ui/react-tabs'
import { PushCategoriesSection } from '@/components/settings/PushCategoriesSection'  // #138
import { ContactsSection } from '@/components/settings/ContactsSection'
import { DefaultEventsViewSection } from '@/components/settings/DefaultEventsViewSection'
import { PushNotificationSection } from '@/components/settings/PushNotificationSection'
import { DisplaySection } from '@/components/settings/DisplaySection'
import { TwoFactorSection } from '@/components/settings/TwoFactorSection'
import { AlertLinkExpirySection } from '@/components/settings/AlertLinkExpirySection'
import { AlertSoundsSection } from '@/components/settings/AlertSoundsSection'  // #294 redesign
import { CriticalSoundSection } from '@/components/settings/CriticalSoundSection'  // #294 redesign
import { usePrefetchOtherTabs, type SettingsTab } from '@/components/settings/prefetch'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/lib/utils'

// ============================================================================
// Settings tabs (item 307)
// ============================================================================
// Notifications · Sounds & display · Security · System. Each tab has its own address
// (/settings?tab=…), so back/forward work and other pages can link straight to a tab.
// Loading never blocks the page: every section draws its frame at once and loads its own data; the open
// tab's data loads first and the other tabs' data in the background (components/settings/prefetch.ts).

/** The tabs this user sees, in order. Security only when it has content (today: super admins → two-factor). */
function visibleSettingsTabs(isSuperuser: boolean): SettingsTab[] {
  return isSuperuser
    ? ['notifications', 'sounds', 'security', 'system']
    : ['notifications', 'sounds']
}

/** Notifications: Contacts, Default Events View, Push Notifications, Notification Categories. */
function NotificationsTab({ isSuperuser }: { isSuperuser: boolean }) {
  return (
    <>
      <ContactsSection />

      {/* Default events view (#248) — super admin only, right below contacts
          (owner placement 2026-10-02, kept in this tab 2026-10-12): the tab the Events page opens on. */}
      {isSuperuser && <DefaultEventsViewSection />}

      {/* Fetches its own camera list; hidden only once loaded with no owned camera. */}
      <PushNotificationSection />

      {/* Notification Categories (#138) — rendered unconditionally: these preferences are
          per-ACCOUNT, not per-camera, so they must not follow the Push section's camera guard. */}
      <PushCategoriesSection />
    </>
  )
}

/** Sounds & display: per-browser choices, every user (no server data to wait for). */
function SoundsTab() {
  return (
    <>
      {/* #294 redesign — Alert sounds: the chime switch and volume, the critical volume (saved in this browser). */}
      <AlertSoundsSection />
      {/* #301 — Display */}
      <DisplaySection />
    </>
  )
}

/** Security (super admins today): two-factor authentication. */
function SecurityTab() {
  return <TwoFactorSection />
}

/** System (super admins): settings that apply to everyone. */
function SystemTab() {
  return (
    <>
      {/* #294 redesign — Critical alert sound, first: which sound, how often it repeats, when it stops
          (its query is also in settingsTabQueries('system'), components/settings/prefetch.ts). */}
      <CriticalSoundSection />
      <AlertLinkExpirySection />
    </>
  )
}

const TAB_TRIGGER = cn(
  // Same pill look as the Events page tab bar (EventFeed).
  'shrink-0 whitespace-nowrap px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
  'data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground',
  'data-[state=active]:bg-amber-400 data-[state=active]:text-amber-950 data-[state=active]:shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

// ============================================================================
// Main Settings Page
// ============================================================================

export function SettingsPage() {
  const { t } = useTranslation('settings')
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false)
  const tabs = visibleSettingsTabs(isSuperuser)
  const [searchParams, setSearchParams] = useSearchParams()
  const asked = searchParams.get('tab')
  const active: SettingsTab = tabs.find((tab) => tab === asked) ?? tabs[0]
  usePrefetchOtherTabs(active, tabs, isSuperuser)

  // An unknown or not-allowed tab (a normal user opening ?tab=system) shows the first tab, and the
  // address is corrected without adding a history entry.
  useEffect(() => {
    if (asked !== null && asked !== active) setSearchParams({ tab: active }, { replace: true })
  }, [asked, active, setSearchParams])

  // One history entry per tab change. Radix selects a tab on mousedown and again from the focus that
  // follows; with the router's v7_startTransition the URL renders a beat later, so the second call would
  // push a duplicate entry (the first Back would then do nothing). The pending tab is ignored until the
  // address has caught up. A click or Enter/Space adds an entry; moving with the arrow keys or Home/End
  // (focus-driven) replaces it, so passing through tabs does not fill the history.
  const pendingTabRef = useRef<string | null>(null)
  const replaceNextRef = useRef(false)
  useEffect(() => { pendingTabRef.current = null }, [active])

  const selectTab = (tab: string) => {
    if (tab === pendingTabRef.current) return
    pendingTabRef.current = tab
    setSearchParams({ tab }, { replace: replaceNextRef.current })
  }
  const onTriggerMouseDown = () => { replaceNextRef.current = false }
  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    replaceNextRef.current = !(e.key === 'Enter' || e.key === ' ')
  }

  const content: Record<SettingsTab, React.ReactNode> = {
    notifications: <NotificationsTab isSuperuser={isSuperuser} />,
    sounds: <SoundsTab />,
    security: <SecurityTab />,
    system: <SystemTab />,
  }

  return (
    // #126 — the nav bar shows "Settings"; no page title here.
    <Tabs.Root
      value={active}
      onValueChange={selectTab}
      className="mx-auto max-w-2xl space-y-6"
    >
      {/* Phone width: the bar scrolls sideways. */}
      <Tabs.List
        aria-label={t('tabs.label')}
        className="flex w-max max-w-full overflow-x-auto rounded-lg border border-border bg-muted/50 p-0.5"
      >
        {tabs.map((tab) => (
          <Tabs.Trigger
            key={tab}
            value={tab}
            className={TAB_TRIGGER}
            onMouseDown={onTriggerMouseDown}
            onKeyDown={onTriggerKeyDown}
          >
            {t(`tabs.${tab}`)}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      {/* Radix mounts only the open tab's content. */}
      {tabs.map((tab) => (
        <Tabs.Content
          key={tab}
          value={tab}
          className="space-y-8 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {content[tab]}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  )
}
